import { ContentSerializeStatus, RelayType } from '@diritto/common/content/constants';
import styled from '@emotion/styled';
import { ButtonType } from 'constants/button';
import { useAuthState } from 'contexts/AuthProvider';
import { useGetRecentlyReadContentQuery } from 'queries/recentlyRead/useGetRecentlyReadContentQuery';
import { usePostRelayContentsQuery } from 'queries/relay/usePostRelayContentsQuery';
import { usePostRelayEpisodesQuery } from 'queries/relay/usePostRelayEpisodesQuery';
import { useReadContinueOrNext } from 'state-hooks/episode/useReadContinueOrNext';
import { ExternalLinkButton } from 'ui-components/common/ExternalLinkButton';
import { LinkButton } from 'ui-components/common/LinkButton';

interface ContentDetailInfoButtonProps {
  className?: string;
  contentId: number;
}

export const ContentDetailInfoButton = ({ className, contentId }: ContentDetailInfoButtonProps) => {
  const { isLoggedIn } = useAuthState();
  const { data: contentsData, isFetched: isContentFetched } = usePostRelayContentsQuery({
    contentIds: [contentId],
    includeAdult: true,
  });

  const { data: recentlyReadContentData } = useGetRecentlyReadContentQuery(contentId, isLoggedIn);

  const recentlyReadEpisodeId = recentlyReadContentData?.recentlyReadContent?.episodeId;

  const { data: episodesData, isFetched: isEpisodeFetched } = usePostRelayEpisodesQuery({
    episodeIds: recentlyReadEpisodeId ? [recentlyReadEpisodeId] : [],
  });

  const content = contentsData && contentsData.contents[contentId];
  const recentlyReadContent = recentlyReadContentData?.recentlyReadContent;
  const episode = episodesData && recentlyReadContent && episodesData.episodes[recentlyReadContent.episodeId];
  const readContinueOrNext = useReadContinueOrNext(content, episode, recentlyReadContent);

  if (!isContentFetched || !isEpisodeFetched) {
    return <StyledButtonWrapper className={className} />;
  }

  if (!readContinueOrNext) {
    return null;
  }

  const publishedLink =
    (content?.relayType === RelayType.Public &&
      content.serializeStatus === ContentSerializeStatus.Published &&
      content.publishedLink.trim()) ||
    undefined;

  return (
    <StyledButtonWrapper className={className}>
      {publishedLink ? (
        <>
          <ExternalLinkButton href={publishedLink} buttonType={ButtonType.Primary} label="출간작 보러 가기" fullWidth />
          <LinkButton
            href={readContinueOrNext.link}
            buttonType={ButtonType.SecondaryNeutral}
            label={readContinueOrNext.buttonText}
            fullWidth
          />
        </>
      ) : (
        <LinkButton
          href={readContinueOrNext.link}
          buttonType={ButtonType.Primary}
          label={readContinueOrNext.buttonText}
          fullWidth
        />
      )}
    </StyledButtonWrapper>
  );
};

const StyledButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  align-items: center;
  left: 0;
  min-height: 48px;
  flex-direction: column;
`;
